// =============================================================================
// DEV caller simulation panel - "call from this number" for browser test calls on /call
// =============================================================================
// Rendered by CallPage.ts. Invisible unless the server says the feature is available for the logged-in user
// (administrator on the personal development runtime; on staging with ZW_CALLER_SIMULATION_STAGING every signed-in
// account, testers included - see db/dev-caller-simulation-resolvers.ts). The number is kept server-side in memory
// per user id and read at the start of EVERY browser call of THAT account - it is not consumed by one call, it lives
// until "Löschen" or a server restart, and nobody else's calls are affected by it.
// Empty/cleared = normal browser call. On staging the panel says so and warns what such a call really does (real CRM
// lookups, real tickets, a real case notification to the contact, visible to ZwickRöll's testers) and what it does
// not do (it stays a browser call: no Twilio number in the call list, no real transfer).
// The server validates and decides; the client check below only gives quicker feedback.
// The display re-reads the server state on page load, when a call starts or ends, when the window gets focus again
// (another tab may have changed it) and after every change, so "aus" does not hide a number the server still uses.
// =============================================================================

import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { devCallerSimulationSetNumber, devCallerSimulationStatus } from '../../db/dev-caller-simulation-resolvers.js';

type SimulationStatus = {
  available: boolean;
  number: string | null;
  envFallback: boolean;
  staging: boolean;
  production: boolean;
};
/** onSavingChange lets CallPage hold the call button while a change is on its way to the server. */
type PanelProps = { callActive: boolean; onSavingChange: (saving: boolean) => void };

const UNAVAILABLE: SimulationStatus = {
  available: false,
  number: null,
  envFallback: false,
  staging: false,
  production: false,
};
const E164 = /^\+[1-9]\d{6,14}$/;

function parseSimulationStatus(raw: unknown): SimulationStatus {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : null;
    if (value?.available !== true) return UNAVAILABLE;
    return {
      available: true,
      number: typeof value.number === 'string' && value.number ? value.number : null,
      envFallback: value.envFallback === true,
      staging: value.environment === 'staging',
      production: value.environment === 'production',
    };
  } catch {
    return UNAVAILABLE;
  }
}

export const DevCallerSimulationPanel = stateful((props: PanelProps) => {
  const [reloadKey, setReloadKey] = useState<number>(0);
  // Mutable holders (no re-render): the reload counter, and the last status the server confirmed.
  const [reloads] = useState<{ count: number }>({ count: 0 });
  const [shown] = useState<{ status: SimulationStatus }>({ status: UNAVAILABLE });
  const statusQuery = useQuery(() => devCallerSimulationStatus()(), [reloadKey, props.callActive]);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const reload = () => setReloadKey(++reloads.count);
    window.addEventListener('focus', reload);
    return () => window.removeEventListener('focus', reload);
  }, []);

  // While a reload is on its way, or if it fails, the last confirmed status stays on screen (hidden until the first).
  if (!statusQuery.loading && !statusQuery.error) shown.status = parseSimulationStatus(statusQuery.value);
  const status = shown.status;
  if (!status.available) return html``;

  const locked = saving || props.callActive;

  const apply = async (callerNumber: string) => {
    setSaving(true);
    props.onSavingChange(true);
    setError(null);
    try {
      shown.status = parseSimulationStatus(await devCallerSimulationSetNumber(callerNumber)());
      setDraft('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
      props.onSavingChange(false);
      setReloadKey(++reloads.count);
    }
  };

  const setNumber = () => {
    const callerNumber = draft.replace(/\s+/g, '');
    if (!E164.test(callerNumber)) {
      setError('Bitte im E.164-Format eingeben: + Ländervorwahl und Nummer, ohne Leerzeichen.');
      return;
    }
    void apply(callerNumber);
  };

  return html`
    <style>
      .zw-simulation-panel {
        box-sizing: border-box;
        width: 100%;
        margin: 0 0 24px;
        padding: 24px;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 3px 14px rgba(26, 34, 48, 0.04);
        color: #202630;
      }
      .zw-simulation-panel .zw-simulation-heading {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }
      .zw-simulation-panel .zw-simulation-title {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .zw-simulation-panel .zw-simulation-icon {
        display: grid;
        place-items: center;
        flex: 0 0 40px;
        width: 40px;
        height: 40px;
        border-radius: 10px;
        background: #fff0f2;
        color: #d6001c;
      }
      .zw-simulation-panel h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        line-height: 1.4;
      }
      .zw-simulation-panel .zw-simulation-subtitle,
      .zw-simulation-panel .zw-simulation-note {
        margin: 3px 0 0;
        color: #667085;
        font-size: 13px;
        line-height: 1.6;
      }
      .zw-simulation-panel .zw-simulation-status {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 10px;
        border: 1px solid #e3e7ed;
        border-radius: 20px;
        background: #f6f7f9;
        color: #566173;
        font-size: 12px;
        font-weight: 700;
      }
      .zw-simulation-panel .zw-simulation-status::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }
      .zw-simulation-panel .zw-simulation-status-active {
        border-color: #a8dcc4;
        background: #edf9f3;
        color: #176641;
      }
      .zw-simulation-panel .zw-simulation-current {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 8px 16px;
        margin: 20px 0 16px;
        padding: 13px 16px;
        border: 1px solid #edf0f4;
        border-radius: 8px;
        background: #f8f9fb;
      }
      .zw-simulation-panel .zw-simulation-label {
        color: #667085;
        font-size: 12px;
        font-weight: 600;
      }
      .zw-simulation-panel .zw-simulation-number {
        color: #202630;
        font:
          600 17px/1.5 ui-monospace,
          SFMono-Regular,
          Consolas,
          monospace;
        overflow-wrap: anywhere;
      }
      .zw-simulation-panel .zw-simulation-number-empty {
        color: #667085;
        font-family: inherit;
        font-size: 14px;
        line-height: 1.5;
      }
      .zw-simulation-panel .zw-simulation-controls {
        display: flex;
        align-items: end;
        flex-wrap: wrap;
        gap: 10px;
        max-width: 860px;
      }
      .zw-simulation-panel .zw-simulation-field {
        flex: 1 1 260px;
        min-width: 0;
        margin: 0;
        color: #344054;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.6;
      }
      .zw-simulation-panel input {
        box-sizing: border-box;
        display: block;
        width: 100%;
        min-width: 0;
        height: 44px;
        margin-top: 6px;
        padding: 9px 12px;
        border: 1px solid #cbd2dc;
        border-radius: 8px;
        background: #fff;
        color: #202630;
        font:
          14px ui-monospace,
          SFMono-Regular,
          Consolas,
          monospace;
      }
      .zw-simulation-panel button {
        box-sizing: border-box;
        min-height: 44px;
        padding: 9px 16px;
        border: 1px solid #d6001c;
        border-radius: 8px;
        background: #d6001c;
        color: #fff;
        font: inherit;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
      }
      .zw-simulation-panel button:hover:not(:disabled) {
        background: #b80018;
        border-color: #b80018;
      }
      .zw-simulation-panel .zw-simulation-clear {
        background: #fff;
        color: #445064;
        border-color: #d5dbe3;
      }
      .zw-simulation-panel .zw-simulation-clear:hover:not(:disabled) {
        background: #f5f7fa;
        border-color: #b8c1ce;
      }
      .zw-simulation-panel :disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }
      .zw-simulation-panel input:focus-visible,
      .zw-simulation-panel button:focus-visible,
      .zw-simulation-panel summary:focus-visible {
        outline: 3px solid #f4b9c2;
        outline-offset: 2px;
      }
      .zw-simulation-panel .zw-simulation-warning {
        display: flex;
        align-items: start;
        gap: 10px;
        margin-top: 18px;
        padding: 12px 14px;
        border: 1px solid #f2d9a6;
        border-radius: 8px;
        background: #fff9ed;
        color: #805316;
        font-size: 13px;
        line-height: 1.6;
      }
      .zw-simulation-panel .zw-simulation-warning svg {
        flex: 0 0 18px;
        margin-top: 2px;
      }
      .zw-simulation-panel details {
        margin-top: 14px;
        color: #667085;
        font-size: 13px;
        line-height: 1.7;
      }
      .zw-simulation-panel summary {
        width: fit-content;
        border-radius: 3px;
        color: #445064;
        font-weight: 600;
        cursor: pointer;
      }
      .zw-simulation-panel details ul {
        max-width: 1000px;
        margin: 10px 0 0;
        padding-left: 20px;
        list-style: disc;
      }
      .zw-simulation-panel details li + li {
        margin-top: 6px;
      }
      .zw-simulation-panel .zw-simulation-note {
        margin-top: 10px;
      }
      .zw-simulation-panel .zw-simulation-error {
        margin: 12px 0 0;
        color: #b00020;
        font-size: 13px;
        line-height: 1.5;
      }
      @media (max-width: 540px) {
        .zw-simulation-panel {
          padding: 18px;
        }
        .zw-simulation-panel .zw-simulation-field {
          flex-basis: 100%;
        }
      }
    </style>
    <section class="zw-simulation-panel" aria-label="Anrufernummer simulieren">
      <div class="zw-simulation-heading">
        <div class="zw-simulation-title">
          <span class="zw-simulation-icon" aria-hidden="true">
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
              <path d="M10 5.5h4M11 18.5h2" />
            </svg>
          </span>
          <div>
            <h3>Anrufernummer simulieren</h3>
            <p class="zw-simulation-subtitle">Browser-Anrufe mit einer Testnummer starten · Nur für dieses Konto</p>
          </div>
        </div>
        ${status.number || status.envFallback
          ? html`<span class="zw-simulation-status zw-simulation-status-active">
              ${status.number ? 'Aktiv' : 'Aktiv · Dev-Vorgabe'}
            </span>`
          : html`<span class="zw-simulation-status">Inaktiv</span>`}
      </div>
      <div class="zw-simulation-current">
        <span class="zw-simulation-label">Aktive Nummer</span>
        ${status.number
          ? html`<span class="zw-simulation-number">${status.number}</span>`
          : html`<span class="zw-simulation-number-empty">
              ${status.envFallback ? 'Dev-Testnummer aus der Umgebung aktiv' : 'Keine · Normaler Browser-Anruf'}
            </span>`}
      </div>
      <div class="zw-simulation-controls">
        <label class="zw-simulation-field">
          Telefonnummer mit Ländervorwahl
          <input
            type="tel"
            placeholder="+49…"
            autocomplete="off"
            .value=${draft}
            @input=${(e: Event) => setDraft((e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter' && !locked && draft.trim()) setNumber();
            }}
            ?disabled=${locked}
          />
        </label>
        <button type="button" @click=${setNumber} ?disabled=${locked || !draft.trim()}>
          ${saving ? 'Speichere…' : 'Setzen'}
        </button>
        <button
          type="button"
          class="zw-simulation-clear"
          @click=${() => void apply('')}
          ?disabled=${locked || !status.number}
        >
          Löschen
        </button>
      </div>
      ${props.callActive
        ? html`<p class="zw-simulation-note">
            Während eines Anrufs gesperrt. Änderungen gelten ab dem nächsten Anruf.
          </p>`
        : nothing}
      ${error ? html`<p class="zw-simulation-error" role="alert">${error}</p>` : nothing}
      <div class="zw-simulation-warning" role="note">
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <path d="M10.3 4.2 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v5M12 17v1" />
        </svg>
        <span>
          <strong>Echtes CRM:</strong> Erstellte Tickets sind echte Fälle; CRM-Kontakte können echte
          Fall-Benachrichtigungen erhalten. Nur dafür freigegebene Kontaktnummern verwenden.
        </span>
      </div>
      <details>
        <summary>Hinweise zur Simulation</summary>
        <ul>
          <li>
            ${status.production
              ? 'Produktion, für alle angemeldeten Konten.'
              : status.staging
                ? 'Staging, für alle angemeldeten Konten.'
                : 'Nur Dev-Runtime, nur für Administratoren.'}
            Die Nummer gilt nur für dieses Konto. Weitere Browser-Anrufe dieses Kontos verwenden sie bis „Löschen“ oder
            Server-Neustart. Anrufe anderer Konten bleiben unberührt.
          </li>
          <li>
            Erkennung, CRM-Abfragen und Tickets verhalten sich wie bei einem echten Anruf von dieser Nummer. Die Nummer
            wird in Call-Sessions und Tickets gespeichert und ist für Benutzer dieser Umgebung sichtbar.
            ${status.staging || status.production ? 'Auch die Tester von ZwickRöll sehen diese Anrufe.' : nothing}
          </li>
          <li>
            Es bleibt ein Browser-Anruf: keine Twilio-Nummer in der Anrufliste und keine echte Weiterleitung. Ohne
            Simulationsnummer wird ein normaler Browser-Anruf gestartet.
            ${status.envFallback
              ? 'Das Panel ist leer, aber die Dev-Vorgabe ZW_DEV_CALLER_SIMULATION ist weiterhin aktiv.'
              : nothing}
          </li>
        </ul>
      </details>
    </section>
  `;
});
